import React, { useState } from "react";
import { FileJson, X, ClipboardPaste } from "lucide-react";
import toast from "react-hot-toast";

interface FieldDesc { key: string; label: string; required?: boolean; }

interface Props {
  onImport: (items: unknown[]) => Promise<void>;
  example: string;
  fields: FieldDesc[];
  onClose: () => void;
  title?: string;
  successMessage?: (n: number) => string;
}

export default function JsonImportBox({ onImport, example, fields, onClose, title, successMessage }: Props) {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handle = async () => {
    setError("");
    let parsed: unknown;
    try {
      parsed = JSON.parse(text.trim());
    } catch {
      setError("JSON không hợp lệ — kiểm tra lại dấu ngoặc, dấu phẩy và dấu nháy.");
      return;
    }
    if (!Array.isArray(parsed) || parsed.length === 0) {
      setError('Phải là mảng JSON: [ {...}, {...} ]');
      return;
    }
    const invalid = (parsed as unknown[]).find(
      (i) => !i || typeof i !== "object" || !("name" in (i as object)) || !(i as Record<string, unknown>).name
    );
    if (invalid) {
      setError('Mỗi phần tử bắt buộc phải có field "name" khác rỗng.');
      return;
    }
    setLoading(true);
    try {
      await onImport(parsed as unknown[]);
      toast.success(successMessage ? successMessage((parsed as unknown[]).length) : `Đã nhập ${(parsed as unknown[]).length} hàng`);
      onClose();
    } catch {
      toast.error("Lỗi khi nhập hàng");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white border border-indigo-200 rounded-2xl shadow-lg overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-indigo-50 border-b border-indigo-100">
        <div className="flex items-center gap-2">
          <FileJson size={16} className="text-indigo-600" />
          <span className="text-sm font-bold text-indigo-800">{title || "Nhập hàng loạt bằng JSON"}</span>
        </div>
        <button onClick={onClose} className="p-1 text-indigo-300 hover:text-indigo-600 touch-manipulation">
          <X size={16} />
        </button>
      </div>

      <div className="p-4 space-y-4">
        {/* Field guide */}
        <div>
          <p className="text-xs font-semibold text-zinc-500 mb-2">Cấu trúc mỗi phần tử:</p>
          <div className="grid grid-cols-1 gap-1">
            {fields.map(f => (
              <div key={f.key} className="flex items-center gap-2 text-xs">
                <code className="bg-zinc-100 text-zinc-700 px-1.5 py-0.5 rounded font-mono">{f.key}</code>
                <span className="text-zinc-500">{f.label}</span>
                {f.required
                  ? <span className="text-red-500 font-semibold">bắt buộc</span>
                  : <span className="text-zinc-300">tuỳ chọn</span>}
              </div>
            ))}
          </div>
        </div>

        {/* Textarea */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-xs font-semibold text-zinc-500">Paste JSON vào đây:</p>
            <button
              onClick={() => { setText(example); setError(""); }}
              className="flex items-center gap-1 text-xs text-indigo-500 hover:text-indigo-700 font-medium touch-manipulation"
            >
              <ClipboardPaste size={12} /> Dán ví dụ
            </button>
          </div>
          <textarea
            className="w-full px-3 py-2.5 border border-zinc-200 rounded-xl text-xs font-mono resize-none focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-zinc-50 placeholder:text-zinc-300"
            rows={7}
            placeholder={example}
            value={text}
            onChange={e => { setText(e.target.value); setError(""); }}
            autoFocus
            spellCheck={false}
          />
          {error && (
            <p className="mt-1.5 text-xs text-red-500 font-medium bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              ⚠ {error}
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <button onClick={onClose}
            className="px-4 py-2.5 border border-zinc-200 rounded-xl text-sm text-zinc-600 touch-manipulation">
            Huỷ
          </button>
          <button onClick={handle} disabled={loading || !text.trim()}
            className="flex-1 py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-bold disabled:opacity-50 touch-manipulation">
            {loading ? "Đang nhập..." : "Nhập hàng"}
          </button>
        </div>
      </div>
    </div>
  );
}
